/**
 * ═══════════════════════════════════════════════════════════════════════════
 * AIDAS Platform — Global Design Token Registry
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Single source of truth for ALL visual design decisions.
 * Consumed by:
 *   • ThemeContext.jsx  → MUI theme + CSS variables
 *   • tailwind.config   → Extended theme tokens
 *   • Core components   → Direct imports for type-safe values
 *   • index.css         → CSS variable fallbacks
 *
 * Rules:
 *   1. NEVER hardcode colors, radii, or spacing in components.
 *   2. Import tokens: `import { tokens } from '@/config/theme';`
 *   3. Tenant branding overrides are applied at RUNTIME via ThemeContext.
 *   4. This file defines the PLATFORM DEFAULTS only.
 * ═══════════════════════════════════════════════════════════════════════════
 */

// ─── Color Palette ──────────────────────────────────────────────────────────

export const colors = {
  /** Core brand colors */
  brand: {
    primary:       '#3b82f6',   // Blue 500  — main CTA, links, active states
    primaryLight:  '#60a5fa',   // Blue 400  — hover accents, light contexts
    primaryDark:   '#2563eb',   // Blue 600  — pressed states, emphasis
    primaryRgb:    '59 130 246',

    secondary:      '#64748b',  // Slate 500 — secondary actions, muted text
    secondaryLight: '#94a3b8',  // Slate 400
    secondaryDark:  '#475569',  // Slate 600
    secondaryRgb:   '100 116 139',

    accent:        '#8b5cf6',   // Violet 500 — AI features, premium markers
    accentLight:   '#a78bfa',   // Violet 400
    accentDark:    '#7c3aed',   // Violet 600
  },

  /** Semantic / status colors */
  semantic: {
    success:       '#10b981',   // Emerald 500
    successLight:  '#34d399',   // Emerald 400
    successBg:     '#ecfdf5',   // Emerald 50
    successBorder: '#a7f3d0',   // Emerald 200

    warning:       '#f59e0b',   // Amber 500
    warningLight:  '#fbbf24',   // Amber 400
    warningBg:     '#fffbeb',   // Amber 50
    warningBorder: '#fde68a',   // Amber 200

    error:         '#ef4444',   // Red 500
    errorLight:    '#f87171',   // Red 400
    errorBg:       '#fef2f2',   // Red 50
    errorBorder:   '#fecaca',   // Red 200

    info:          '#3b82f6',   // Blue 500
    infoLight:     '#60a5fa',   // Blue 400
    infoBg:        '#eff6ff',   // Blue 50
    infoBorder:    '#bfdbfe',   // Blue 200
  },

  /** Neutral / grayscale palette (Slate-based) */
  neutral: {
    0:   '#ffffff',
    25:  '#fcfcfd',
    50:  '#f8fafc',
    100: '#f1f5f9',
    150: '#e8edf4',
    200: '#e2e8f0',
    300: '#cbd5e1',
    400: '#94a3b8',
    500: '#64748b',
    600: '#475569',
    700: '#334155',
    800: '#1e293b',
    900: '#0f172a',
    950: '#020617',
  },

  /** Overlay & backdrop */
  overlay: {
    light:   'rgba(0, 0, 0, 0.4)',
    medium:  'rgba(0, 0, 0, 0.55)',
    heavy:   'rgba(0, 0, 0, 0.75)',
    scrim:   'rgba(15, 23, 42, 0.6)',    // Dark slate scrim
  },
} as const;


// ─── Light Theme ────────────────────────────────────────────────────────────

export const lightTheme = {
  // Surfaces
  bg: {
    page:        colors.neutral[50],       // #f8fafc — main page background
    surface:     colors.neutral[0],        // #ffffff — cards, panels
    surfaceAlt:  colors.neutral[100],      // #f1f5f9 — alternating rows, subtle sections
    elevated:    colors.neutral[0],        // #ffffff — dialogs, dropdowns (with shadow)
    muted:       colors.neutral[100],      // #f1f5f9 — disabled bg, code blocks
    inverse:     colors.neutral[900],      // #0f172a — tooltips, inverted badges
  },

  // Text
  text: {
    primary:     colors.neutral[800],      // #1e293b — headings, primary content
    secondary:   colors.neutral[500],      // #64748b — descriptions, help text
    tertiary:    colors.neutral[400],      // #94a3b8 — placeholders, captions
    disabled:    colors.neutral[300],      // #cbd5e1 — disabled text
    inverse:     colors.neutral[0],        // #ffffff — text on dark bg
    link:        colors.brand.primary,     // #3b82f6 — clickable text
  },

  // Borders
  border: {
    default:     colors.neutral[200],      // #e2e8f0 — standard borders
    light:       colors.neutral[100],      // #f1f5f9 — subtle dividers
    strong:      colors.neutral[300],      // #cbd5e1 — emphasis borders
    focus:       colors.brand.primary,     // #3b82f6 — focus rings
    error:       colors.semantic.error,    // #ef4444 — validation errors
  },

  // Sidebar
  sidebar: {
    bg:          colors.neutral[0],        // #ffffff
    text:        colors.neutral[800],      // #1e293b
    textMuted:   colors.neutral[500],      // #64748b
    activeItem:  `rgba(${colors.brand.primaryRgb} / 0.08)`,
    hoverItem:   colors.neutral[50],       // #f8fafc
  },

  // Header
  header: {
    bg:          colors.neutral[0],        // #ffffff
    border:      colors.neutral[200],      // #e2e8f0
  },

  // Scrollbar
  scrollbar: {
    thumb:       colors.neutral[300],      // #cbd5e1
    thumbHover:  colors.neutral[400],      // #94a3b8
    track:       'transparent',
  },
} as const;


// ─── Dark Theme ─────────────────────────────────────────────────────────────

export const darkTheme = {
  // Surfaces
  bg: {
    page:        colors.neutral[900],      // #0f172a
    surface:     colors.neutral[800],      // #1e293b
    surfaceAlt:  colors.neutral[700],      // #334155
    elevated:    colors.neutral[800],      // #1e293b (with deeper shadow)
    muted:       colors.neutral[700],      // #334155
    inverse:     colors.neutral[100],      // #f1f5f9
  },

  // Text
  text: {
    primary:     colors.neutral[100],      // #f1f5f9
    secondary:   colors.neutral[400],      // #94a3b8
    tertiary:    colors.neutral[500],      // #64748b
    disabled:    colors.neutral[600],      // #475569
    inverse:     colors.neutral[900],      // #0f172a
    link:        colors.brand.primaryLight, // #60a5fa
  },

  // Borders
  border: {
    default:     colors.neutral[700],      // #334155
    light:       colors.neutral[800],      // #1e293b
    strong:      colors.neutral[600],      // #475569
    focus:       colors.brand.primaryLight, // #60a5fa
    error:       colors.semantic.errorLight,// #f87171
  },

  // Sidebar
  sidebar: {
    bg:          colors.neutral[900],      // #0f172a
    text:        colors.neutral[200],      // #e2e8f0
    textMuted:   colors.neutral[400],      // #94a3b8
    activeItem:  `rgba(${colors.brand.primaryRgb} / 0.15)`,
    hoverItem:   colors.neutral[800],      // #1e293b
  },

  // Header
  header: {
    bg:          colors.neutral[800],      // #1e293b
    border:      colors.neutral[700],      // #334155
  },

  // Scrollbar
  scrollbar: {
    thumb:       colors.neutral[700],      // #334155
    thumbHover:  colors.neutral[600],      // #475569
    track:       'transparent',
  },
} as const;


// ─── Border Radius ──────────────────────────────────────────────────────────

export const radius = {
  none:   '0px',
  xs:     '2px',
  sm:     '4px',       // Compact inputs, small chips
  md:     '6px',       // Default inputs, buttons
  lg:     '8px',       // Cards, containers
  xl:     '12px',      // Larger cards, modals
  '2xl':  '16px',      // Hero sections, large panels
  '3xl':  '24px',      // Feature cards
  full:   '9999px',    // Circular avatars, pill badges
} as const;

/** Platform default border radius (px value for MUI / CSS var) */
export const DEFAULT_BORDER_RADIUS = 8;


// ─── Typography ─────────────────────────────────────────────────────────────

export const typography = {
  /** Font families */
  fontFamily: {
    sans:     "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    mono:     "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
    display:  "'Inter', sans-serif",
  },

  /** Available font options for tenant branding */
  availableFonts: [
    'Inter',
    'Poppins',
    'Roboto',
    'Open Sans',
    'Lora',
    'Roboto Slab',
    'Inconsolata',
    'JetBrains Mono',
  ],

  /** Font sizes (rem-based, 14px root) */
  fontSize: {
    '2xs':    '0.625rem',   // 10px — micro labels
    xs:       '0.714rem',   // ~10px at 14px root — timestamps, fine print
    sm:       '0.857rem',   // ~12px — captions, helper text
    base:     '1rem',       // 14px — body text (root = 14px)
    md:       '1.071rem',   // ~15px — slightly emphasized body
    lg:       '1.143rem',   // ~16px — section titles
    xl:       '1.286rem',   // ~18px — card titles, sub-headings
    '2xl':    '1.571rem',   // ~22px — page headings
    '3xl':    '2rem',       // 28px — hero headings
    '4xl':    '2.571rem',   // ~36px — display headings
  },

  /** Font weights */
  fontWeight: {
    light:      300,
    normal:     400,
    medium:     500,
    semibold:   600,
    bold:       700,
    extrabold:  800,
  },

  /** Line heights */
  lineHeight: {
    none:     1,
    tight:    1.25,
    snug:     1.375,
    normal:   1.5,
    relaxed:  1.625,
    loose:    2,
  },

  /** Letter spacing */
  letterSpacing: {
    tighter:  '-0.05em',
    tight:    '-0.025em',
    normal:   '0em',
    wide:     '0.025em',
    wider:    '0.05em',
    widest:   '0.1em',
  },
} as const;


// ─── Spacing Scale ──────────────────────────────────────────────────────────

export const spacing = {
  0:    '0px',
  0.5:  '2px',
  1:    '4px',
  1.5:  '6px',
  2:    '8px',
  2.5:  '10px',
  3:    '12px',
  3.5:  '14px',
  4:    '16px',
  5:    '20px',
  6:    '24px',
  7:    '28px',
  8:    '32px',
  9:    '36px',
  10:   '40px',
  12:   '48px',
  14:   '56px',
  16:   '64px',
  20:   '80px',
  24:   '96px',
} as const;


// ─── Shadows ────────────────────────────────────────────────────────────────

export const shadows = {
  none:       'none',
  xs:         '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  sm:         '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
  md:         '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  lg:         '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -4px rgba(0, 0, 0, 0.1)',
  xl:         '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
  '2xl':      '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
  inner:      'inset 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
  outline:    '0 0 0 3px rgba(59, 130, 246, 0.3)',     // Focus ring
  glow:       '0 0 20px rgba(59, 130, 246, 0.15)',      // Brand accent glow
  card:       '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.06)',
  cardHover:  '0 10px 30px rgba(0,0,0,0.08), 0 4px 8px rgba(0,0,0,0.04)',
  dialog:     '0 16px 48px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)',
  dropdown:   '0 4px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)',
} as const;

export const darkShadows = {
  ...shadows,
  outline:    '0 0 0 3px rgba(96, 165, 250, 0.35)',
  glow:       '0 0 20px rgba(96, 165, 250, 0.2)',
  card:       '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
  cardHover:  '0 10px 30px rgba(0,0,0,0.4), 0 4px 8px rgba(0,0,0,0.2)',
  dialog:     '0 16px 48px rgba(0,0,0,0.5), 0 4px 12px rgba(0,0,0,0.3)',
  dropdown:   '0 4px 24px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.3)',
} as const;


// ─── Z-Index ────────────────────────────────────────────────────────────────

export const zIndex = {
  hide:       -1,
  base:       0,
  dropdown:   1000,
  sticky:     1100,
  sidebar:    1200,
  header:     1300,
  overlay:    1400,
  modal:      1500,
  popover:    1600,
  toast:      1700,
  tooltip:    1800,
  spotlight:  1900,     // AI spotlight, command palette
  max:        9999,
} as const;


// ─── Breakpoints ────────────────────────────────────────────────────────────

export const breakpoints = {
  xs:   '475px',
  sm:   '640px',
  md:   '768px',
  lg:   '1024px',
  xl:   '1280px',
  '2xl': '1536px',
} as const;


// ─── Transitions ────────────────────────────────────────────────────────────

export const transitions = {
  /** Duration presets (ms) */
  duration: {
    instant:  75,
    fast:     150,
    normal:   200,
    moderate: 300,
    slow:     500,
    glacial:  700,
  },

  /** Easing presets */
  easing: {
    default:    'cubic-bezier(0.4, 0, 0.2, 1)',     // ease-in-out
    in:         'cubic-bezier(0.4, 0, 1, 1)',
    out:        'cubic-bezier(0, 0, 0.2, 1)',
    inOut:      'cubic-bezier(0.4, 0, 0.2, 1)',
    spring:     'cubic-bezier(0.34, 1.56, 0.64, 1)', // bouncy
    snap:       'cubic-bezier(0.2, 0, 0, 1)',         // sharp snap-in
  },

  /** Common presets */
  preset: {
    fade:         'opacity 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slideUp:      'transform 300ms cubic-bezier(0, 0, 0.2, 1), opacity 300ms cubic-bezier(0, 0, 0.2, 1)',
    scale:        'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
    color:        'color 150ms ease, background-color 150ms ease, border-color 150ms ease',
    all:          'all 200ms cubic-bezier(0.4, 0, 0.2, 1)',
    allSlow:      'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },
} as const;


// ─── Component Size Scale ───────────────────────────────────────────────────

export const componentSizes = {
  /** Input / button heights */
  inputHeight: {
    xs:   '28px',
    sm:   '32px',
    md:   '36px',
    lg:   '40px',
    xl:   '44px',
  },

  /** Icon sizes */
  iconSize: {
    xs:   '12px',
    sm:   '14px',
    md:   '16px',
    lg:   '20px',
    xl:   '24px',
    '2xl': '32px',
  },

  /** Avatar sizes */
  avatarSize: {
    xs:   '24px',
    sm:   '32px',
    md:   '40px',
    lg:   '48px',
    xl:   '64px',
    '2xl': '80px',
  },

  /** Layout widths */
  layout: {
    sidebarCollapsed: '64px',
    sidebarExpanded:  '260px',
    headerHeight:     '56px',
    maxContentWidth:  '1440px',
    maxFormWidth:     '640px',
    maxDialogWidth:   '560px',
  },
} as const;


// ─── Chart Theme ────────────────────────────────────────────────────────────

export const chartColors = {
  default: [
    '#3b82f6',  // Blue 500
    '#8b5cf6',  // Violet 500
    '#10b981',  // Emerald 500
    '#f59e0b',  // Amber 500
    '#ef4444',  // Red 500
    '#ec4899',  // Pink 500
    '#06b6d4',  // Cyan 500
    '#84cc16',  // Lime 500
    '#f97316',  // Orange 500
    '#6366f1',  // Indigo 500
  ],
  pastel: [
    '#93c5fd',  // Blue 300
    '#c4b5fd',  // Violet 300
    '#6ee7b7',  // Emerald 300
    '#fcd34d',  // Amber 300
    '#fca5a5',  // Red 300
    '#f9a8d4',  // Pink 300
    '#67e8f9',  // Cyan 300
    '#bef264',  // Lime 300
    '#fdba74',  // Orange 300
    '#a5b4fc',  // Indigo 300
  ],
  corporate: [
    '#1e40af',  // Blue 800
    '#3730a3',  // Indigo 800
    '#065f46',  // Emerald 800
    '#92400e',  // Amber 800
    '#991b1b',  // Red 800
    '#831843',  // Pink 800
  ],
} as const;


// ─── Consolidated Default Theme Config ──────────────────────────────────────

export const DEFAULT_THEME_CONFIG = {
  mode:            'light' as 'light' | 'dark' | 'system',
  primaryColor:    colors.brand.primary,
  secondaryColor:  colors.brand.secondary,
  shape:           'rounded' as 'rounded' | 'pill' | 'square',
  borderRadius:    DEFAULT_BORDER_RADIUS,
  font:            'Inter',
  sidebarTheme:    { bg: null as string | null, text: null as string | null },
  headerTheme:     { bg: null as string | null },
  chartTheme:      'default' as keyof typeof chartColors,
} as const;


// ─── Aggregate Export ───────────────────────────────────────────────────────

export const tokens = {
  colors,
  lightTheme,
  darkTheme,
  radius,
  typography,
  spacing,
  shadows,
  darkShadows,
  zIndex,
  breakpoints,
  transitions,
  componentSizes,
  chartColors,
} as const;

export type ThemeMode = 'light' | 'dark' | 'system';
export type ShapeVariant = 'rounded' | 'pill' | 'square';
export type ThemeTokens = typeof tokens;

export default tokens;
